import axios, { HttpStatusCode } from "axios";
import { useRef, useTransition } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useSetRecoilState } from "recoil";

import {
  apiBackgroundLoadingErrorsAtom,
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  ApiSuffixType,
  castErrorResult,
  ConfirmResult,
  createErrorResult,
  createSuccessResult,
  CrudApi,
  DatesUnit,
  debugModeToApiParam,
  editFormDataAF,
  emptyLiveMapGridToolbarStats,
  errors,
  formatGroupedProcessErrorLine,
  getChunkErrorsFromResult,
  getLiveMapStatsFromRideSearchRows,
  groupProcessErrorsByReason,
  hasKeys,
  IApiError,
  IRideArchiveApiPayload,
  IRideAssignDocksPayload,
  IRideCancelApiPayload,
  IRideDto,
  IRideMetadata,
  IRideTableDto,
  ISearchCriteria,
  isSoftHardValidationSingleErrorResponse,
  isSoftValidationSingleErrorResponse,
  isTranslatableItem,
  isValidDateTime,
  ITranslatableItem,
  lastSearchCriteriaAtomFamily,
  liveMapGridToolbarStatsAtomFamily,
  LiveMapRideFilterEnum,
  loadingEditFormContentAtomFamily,
  Log,
  mapHardValidationErrorToResult,
  mergeLiveMapPollRides,
  nearbyFilteringRideIdAtom,
  normalizeErrors,
  normalizeRideEntity,
  pendingRideFetchAtom,
  rideConvertToDynamic,
  RideCrudModeKey,
  RideCrudModeKeyTuple,
  RideDeleteActionsResultEnum,
  RideEntity,
  RideFetchDataMode,
  RidesApi,
  rideSoftValidationRequiresConvertToDynamic,
  RideUpdateDriverOrVehiclePatch,
  RideUpdateModel,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultErrorTechnicalError,
  simpleResultInvalidExecutionResult,
  SortEnumAPI,
  tableRowsAtomFamily,
  TError,
  toastGroupedProcessErrors,
  toForbiddenTranslatableItem,
  translateInfoItem,
  TRideUpdateDriverOrVehicleDto,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";
import { useModalConfirm } from "../../hooks-singletons";
import { useDynamicTablesCleanup } from "../dynamic-tables";
import { useComposedSearchCriteria } from "../search";
import { useGlobalEffectActions } from "../use-global-effect-actions";
import { useIsFetchAllowed } from "../use-is-fetch-allowed";
import { useSelectedIds } from "../use-selected-ids";
import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";
import { useClearStaleRideEditDto } from "./use-clear-stale-ride-edit-dto";
import { useRideNearbyActions } from "./use-ride-nearby-actions";
import { useRidesListActions } from "./use-rides-list-actions";

export interface IFetchRidesOptions {
  abortController?: AbortController;
  backgroundAction?: boolean;
  debugMode?: boolean;
  dataMode?: RideFetchDataMode;
  isBypassPreloader?: boolean;
  skipPageReset?: boolean;
  skipCollapseGridPanels?: boolean;
  skipResetStarredRows?: boolean;
  datesRangeOverride?: DatesUnit;
  liveMapPlannedForNow?: boolean;
  liveMapRideFilter?: LiveMapRideFilterEnum;
}

export const useRidesActions = () => {
  const [rideDto, setRideDtoGlobal] = useRecoilState<
    Partial<IRideDto> | undefined
  >(editFormDataAF(RideCrudModeKeyTuple));
  const { entity: entityFromContext } = usePageContext();
  const entity = normalizeRideEntity(entityFromContext);
  const { t } = useTranslation();
  const setRides = useSetRecoilState(tableRowsAtomFamily(entity));
  const setLiveMapGridToolbarStats = useSetRecoilState(
    liveMapGridToolbarStatsAtomFamily(entity),
  );
  const [, startTransition] = useTransition();
  const ridesFetchingDepthRef = useRef(0);
  const setErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setPendingRideFetch = useSetRecoilState(pendingRideFetchAtom);
  const setBackgroundLoadingErrors = useSetRecoilState(
    apiBackgroundLoadingErrorsAtom,
  );
  const setRidesFetching = useSetRecoilState(apiDataFetchingAtom);
  const setRideLoading = useSetRecoilState(
    loadingEditFormContentAtomFamily(RideCrudModeKey.uiUnit),
  );

  const { collapseExpandedRowsAppEvent } = useGlobalEffectActions();
  const { removeSelectedId, synchronizeSelectedRowsOnSearch } =
    useSelectedIds();
  const { replaceRide, replaceRides, removeRide } = useRidesListActions();
  const { clearStaleRideEditDtoCacheForRide } = useClearStaleRideEditDto();

  const setNearbyFilteringRideId = useSetRecoilState(nearbyFilteringRideIdAtom);
  const { confirmDialogResult } = useModalConfirm();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();

  const { prepareServerSideTableFetch } = useDynamicTablesCleanup({
    entity: entityFromContext,
  });

  const { getIsFetchAllowed } = useIsFetchAllowed({
    searchQueryToOptimize: "rides",
  });
  const { resetNearbyCache, refetchAllNearbyByRideAndCurrent } =
    useRideNearbyActions();
  const { getDynamicFormsComposedCriteria, getLiveMapComposedCriteria } =
    useComposedSearchCriteria(entity);

  const setLastSearchCriteria = useSetRecoilState(
    lastSearchCriteriaAtomFamily(entity),
  );

  const fetchRides = useEvent(
    async (
      fetchOptions?: IFetchRidesOptions,
    ): Promise<SimpleResult<IRideTableDto[]>> => {
      const {
        abortController,
        backgroundAction,
        debugMode,
        dataMode,
        skipPageReset,
        skipCollapseGridPanels,
        isBypassPreloader,
        datesRangeOverride,
        liveMapPlannedForNow,
        liveMapRideFilter,
      } = fetchOptions ?? {};

      try {
        if (backgroundAction) {
          setBackgroundLoadingErrors(undefined);
        } else {
          setErrors(undefined);

          if (!skipCollapseGridPanels) {
            collapseExpandedRowsAppEvent();
          }

          prepareServerSideTableFetch({
            skipPageReset,
          });

          resetNearbyCache();
          setNearbyFilteringRideId(null);

          if (!isBypassPreloader) {
            ridesFetchingDepthRef.current += 1;
            setRidesFetching(true);
          }
        }

        const composedQueryObject = datesRangeOverride
          ? getLiveMapComposedCriteria(datesRangeOverride)
          : getDynamicFormsComposedCriteria();
        setLastSearchCriteria(composedQueryObject);
        const isRideMapPage = entityFromContext === "rideMap";
        const resolvedDataMode =
          dataMode ?? (isRideMapPage ? RideFetchDataMode.LiveMap : undefined);
        const body: ISearchCriteria<RideEntity> = {
          where: composedQueryObject,
          sort: SortEnumAPI.SortById,
          debugMode: debugModeToApiParam(debugMode),
          dataMode: resolvedDataMode,
          ...(liveMapPlannedForNow ? { liveMapPlannedForNow: true } : {}),
          ...(liveMapRideFilter ? { liveMapRideFilter } : {}),
        };

        const isFetchAllowed = await getIsFetchAllowed(body);

        if (!isFetchAllowed) {
          return new SimpleResult({
            error: t(errors.common.fetchNotAllowed),
          });
        }

        const ridesSearchResult = await CrudApi.post<
          SimpleResult<IRideTableDto[]>
        >({
          entity: "ride",
          data: body,
          suffixType: ApiSuffixType.ServerSearch,
          abortController,
        });

        if (!ridesSearchResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        if (
          !ridesSearchResult.success ||
          (ridesSearchResult.success && !ridesSearchResult.data)
        ) {
          const error = ridesSearchResult.error;

          if (isTranslatableItem(error)) {
            setErrors([translateInfoItem(error, t)]);
          } else {
            setErrors([t(errors.ride.failedFetchingRides)]);
          }

          return castErrorResult(ridesSearchResult);
        }

        const dtos = ridesSearchResult.data ?? [];
        const isLiveMapFetch =
          resolvedDataMode === RideFetchDataMode.LiveMap || isRideMapPage;
        const shouldMergeLiveMapPoll = Boolean(
          backgroundAction && isLiveMapFetch,
        );

        Log.debug("Rides dto received: ", dtos);
        /* synchronization is disabled for now, but can be needed in the future
        synchroStarredWithRows(dtos); */

        const applyRideRowsUpdate = (): void => {
          if (shouldMergeLiveMapPoll) {
            setRides(prev => {
              const { merged, changed } = mergeLiveMapPollRides(
                prev as IRideTableDto[] | undefined,
                dtos,
              );

              return changed ? merged : prev;
            });
          } else {
            setRides(dtos);
          }

          if (isLiveMapFetch) {
            setLiveMapGridToolbarStats(
              getLiveMapStatsFromRideSearchRows(dtos) ??
                emptyLiveMapGridToolbarStats(),
            );
          }
        };

        if (shouldMergeLiveMapPoll) {
          startTransition(applyRideRowsUpdate);
        } else {
          applyRideRowsUpdate();
        }

        return createSuccessResult(dtos);
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return new SimpleResult({
            error: normalizeErrors(error as TError, t),
          });
        }

        Log.errorAndToast(t("errors.failedFetchingRides"), error);

        if (backgroundAction) {
          setErrors(normalizeErrors(error as IApiError, t));
        } else {
          setBackgroundLoadingErrors(normalizeErrors(error as IApiError, t));
        }

        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      } finally {
        setPendingRideFetch(false);

        if (!backgroundAction && !isBypassPreloader) {
          ridesFetchingDepthRef.current = Math.max(
            0,
            ridesFetchingDepthRef.current - 1,
          );
          if (ridesFetchingDepthRef.current === 0) {
            setRidesFetching(false);
          }
        }
      }
    },
  );

  const refetchRide = useEvent(
    async (rideId: number): Promise<SimpleResult<IRideDto>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);
        const dto = await RidesApi.fetchRide(rideId);

        if (!dto) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        Log.debug("Rides dto received: ", dto);
        replaceRide(dto);
        return new SimpleResult({ success: true, data: dto });
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const deleteRide = useEvent(
    async (
      rideId: number,
      bookingAction: RideDeleteActionsResultEnum,
    ): Promise<SimpleResult<number>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);

        const apiPayload: IRideArchiveApiPayload = {
          bookingAction,
        };

        let result = await RidesApi.deleteRide(rideId, apiPayload);

        if (isSoftValidationSingleErrorResponse(result?.data)) {
          setRideLoading(false);

          const softValidationResult = await confirmSoftValidationAction(
            result.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setRideLoading(true);
          apiPayload.token = result.data.token;
          result = await RidesApi.deleteRide(rideId, apiPayload);
        } else if (isSoftHardValidationSingleErrorResponse(result?.data)) {
          const hardValidationError = result.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!result?.success || !result.data) {
          if (!result?.error || !isTranslatableItem(result.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([t((result?.error as ITranslatableItem).translationKey)]);
            return castErrorResult(result);
          }
        }

        Log.debug("Ride deleted with id: ", rideId);
        removeRide(rideId);
        removeSelectedId(rideId);

        return new SimpleResult({ success: true, data: rideId });
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const createRide = useEvent(
    async (payload: RideUpdateModel): Promise<SimpleResult<IRideDto>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);

        let createResult = await RidesApi.postRide(payload);

        if (isSoftValidationSingleErrorResponse(createResult?.data)) {
          setRideLoading(false);

          const softValidationResult = await confirmSoftValidationAction(
            createResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setRideLoading(true);
          payload.token = createResult.data.token;
          createResult = await RidesApi.postRide(payload);
        } else if (
          isSoftHardValidationSingleErrorResponse(createResult?.data)
        ) {
          const hardValidationError = createResult.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!createResult?.success || !createResult.data) {
          if (!createResult?.error || !isTranslatableItem(createResult.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([
              t((createResult?.error as ITranslatableItem).translationKey),
            ]);
            return castErrorResult(createResult);
          }
        }

        const createResultRide = createResult as SimpleResult<IRideDto>;

        if (!payload.dryRun) {
          const rideId = createResultRide.data?.id as number;
          refetchAllNearbyByRideAndCurrent(rideId);

          Log.debug(`Ride successfully created!`, createResult); // TODO? Add translation key
        } else {
          Log.debug(`Ride creation checked by api (dry-run)`, createResult);
        }

        return createResultRide;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: error as TError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const updateRide = useEvent(
    async ({
      rideForApiSend,
      rideId,
    }: {
      rideForApiSend: RideUpdateModel;
      rideId: number;
    }): Promise<SimpleResult<Partial<IRideDto>>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);

        let rideUpdatedResult = await RidesApi.patchRide({
          rideId,
          rideForApiSend,
        });

        if (isSoftValidationSingleErrorResponse(rideUpdatedResult?.data)) {
          setRideLoading(false);
          Log.debug(
            "Soft validation case on ride update, response data:",
            rideUpdatedResult,
          );
          const softValidationResult = await confirmSoftValidationAction(
            rideUpdatedResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return createSuccessResult();
          }

          setRideLoading(true);
          // Token only skips soft-validation; flex convert still needs courseId cleared in the payload (BE mergeRideClearingRemovedCourse).
          const shouldConvertToDynamic =
            rideSoftValidationRequiresConvertToDynamic(
              rideUpdatedResult.data.validationErrors,
            );
          const convertedRideForApiSend = shouldConvertToDynamic
            ? rideConvertToDynamic(rideForApiSend)
            : undefined;
          const rideForApiSendAfterConfirm: RideUpdateModel = {
            ...(convertedRideForApiSend ?? rideForApiSend),
            token: rideUpdatedResult.data.token,
          };
          rideUpdatedResult = await RidesApi.patchRide({
            rideId,
            rideForApiSend: rideForApiSendAfterConfirm,
          });
        } else if (
          isSoftHardValidationSingleErrorResponse(rideUpdatedResult?.data)
        ) {
          Log.debug(
            "Soft-hard validation case on ride update, response data:",
            rideUpdatedResult,
          );
          const hardValidationError =
            rideUpdatedResult.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!rideUpdatedResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        if (!rideUpdatedResult.success) {
          return castErrorResult(rideUpdatedResult);
        }

        if (!rideUpdatedResult.data) {
          return createErrorResult(errors.common.invalidEntityReturnedFromAPI);
        }

        if (!rideForApiSend.dryRun) {
          Log.debug(`Ride successfully updated!`, rideUpdatedResult.data);
          refetchAllNearbyByRideAndCurrent(rideId);
        } else {
          Log.debug(
            `Ride update checked by api (dry-run)`,
            rideUpdatedResult.data,
          );
        }

        return rideUpdatedResult as SimpleResult<IRideDto>;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        setErrors(normalizedError);
        return new SimpleResult({ error: error as TError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const updateRideStartEndDate = useEvent(
    async ({
      rideId,
      rideStartDate,
      rideEndDate,
      metadata,
      rideTypeId,
      confirmMessageTranslationKey = "ride.UPDATE_CONFIRM_DEFAULT_DATE_MESSAGE",
      isBypassConfirmation = true,
      isReturnDtoForGrid,
    }: {
      rideId: number;
      rideStartDate?: TDateISO;
      rideEndDate?: TDateISO;
      metadata: IRideMetadata;
      rideTypeId?: number | null;
      confirmMessageTranslationKey?: string;
      isBypassConfirmation?: boolean;
      isReturnDtoForGrid?: boolean;
    }): Promise<SimpleResult<IRideDto>> => {
      if (rideStartDate && !isValidDateTime(rideStartDate)) {
        return createErrorResult(errors.ride.invalidRideDate);
      }

      if (rideEndDate && !isValidDateTime(rideEndDate)) {
        return createErrorResult(errors.ride.invalidRideDate);
      }

      if (!rideId) {
        return createErrorResult(errors.ride.invalidRideId);
      }

      if (!isBypassConfirmation) {
        const res = await confirmDialogResult({
          title: t("general.UPDATE_CONFIRM"),
          message: t(confirmMessageTranslationKey),
        });

        if (res !== ConfirmResult.OK) {
          return createErrorResult(errors.ride.updatedRideCanceledByUser);
        }
      }

      try {
        setRideLoading(true);
        const rideForApiSend: RideUpdateModel = {
          ...(rideStartDate && { rideStart: rideStartDate }),
          ...(rideEndDate && { rideEnd: rideEndDate }),
          ...(rideTypeId && { rideTypeId }),
          metadata,
          isReturnDtoForGrid,
        };

        let rideUpdatedResult = await RidesApi.patchRide({
          rideId,
          rideForApiSend,
        });

        if (isSoftValidationSingleErrorResponse(rideUpdatedResult?.data)) {
          setRideLoading(false);

          const softValidationResult = await confirmSoftValidationAction(
            rideUpdatedResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setRideLoading(true);
          rideForApiSend.token = rideUpdatedResult.data.token;
          rideUpdatedResult = await RidesApi.patchRide({
            rideId,
            rideForApiSend,
          });
        } else if (
          isSoftHardValidationSingleErrorResponse(rideUpdatedResult?.data)
        ) {
          const hardValidationError =
            rideUpdatedResult.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!rideUpdatedResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        if (!rideUpdatedResult.success) {
          return castErrorResult(rideUpdatedResult);
        }

        if (!rideUpdatedResult.data) {
          return simpleResultInvalidExecutionResult(t);
        }

        replaceRide(rideUpdatedResult.data as IRideDto);
        refetchAllNearbyByRideAndCurrent(rideId);

        if (isReturnDtoForGrid) {
          clearStaleRideEditDtoCacheForRide(rideId);
        } else if (rideDto?.id === rideId) {
          setRideDtoGlobal(rideUpdatedResult.data as IRideDto);
        }

        Log.debug("Ride updated successfully");
        return rideUpdatedResult as SimpleResult<IRideDto>;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const cancelRide = useEvent(
    async (
      id: number,
      bookingAction: RideDeleteActionsResultEnum,
      cancelationReason?: string,
    ): Promise<SimpleResult<IRideDto>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);
        const apiPayload: IRideCancelApiPayload = {
          bookingAction,
          cancelationReason,
        };

        let result = await RidesApi.cancelRide(id, apiPayload);

        if (isSoftValidationSingleErrorResponse(result?.data)) {
          setRideLoading(false);

          const softValidationResult = await confirmSoftValidationAction(
            result.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setRideLoading(true);
          apiPayload.token = result.data.token;
          result = await RidesApi.cancelRide(id, apiPayload);
        } else if (isSoftHardValidationSingleErrorResponse(result?.data)) {
          const hardValidationError = result.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!result?.success || !result?.data) {
          if (!result?.error || !isTranslatableItem(result.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([t((result?.error as ITranslatableItem).translationKey)]);
            return castErrorResult(result);
          }
        }

        return result as SimpleResult<IRideDto>;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        const displayError = normalizedError?.length
          ? normalizedError
          : [t("errors.failedToCancelRides")];
        setErrors(displayError);
        return new SimpleResult({ error: displayError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const renewRide = useEvent(
    async (id: number): Promise<SimpleResult<IRideDto>> => {
      try {
        setErrors(undefined);
        setRideLoading(true);

        const result = await RidesApi.bulkRenewRides({ ids: [id] });

        if (!result?.success || !Array.isArray(result?.data)) {
          const chunkErrors = result ? getChunkErrorsFromResult(result) : [];
          if (result && chunkErrors.length) {
            toastGroupedProcessErrors({
              errors: chunkErrors,
              translationFunction: t,
            });
            setErrors(
              groupProcessErrorsByReason(chunkErrors).map(group =>
                formatGroupedProcessErrorLine(group, t),
              ),
            );
            return castErrorResult(result);
          }
          if (!result?.error || !isTranslatableItem(result.error)) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([t(result.error.translationKey)]);
            return castErrorResult(result);
          }
        }

        return createSuccessResult(result.data[0]);
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        const displayError = normalizedError?.length
          ? normalizedError
          : [t(errors.common.technicalProblemOccurred)];
        setErrors(displayError);
        return new SimpleResult({ error: displayError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  const assignDocks = useEvent(
    async (
      rideIds: number[],
      dataMode?: RideFetchDataMode,
    ): Promise<SimpleResult<void>> => {
      try {
        setBackgroundLoadingErrors(undefined);

        const composedQueryObject = getDynamicFormsComposedCriteria();
        const body: IRideAssignDocksPayload = {
          rideIds,
          dataMode,
        };

        const assignDocksResult = await RidesApi.assignDocks(body);

        if (!assignDocksResult?.success || !assignDocksResult.data) {
          if (
            !assignDocksResult?.error ||
            !isTranslatableItem(assignDocksResult.error)
          ) {
            setErrors([t(errors.common.technicalProblemOccurred)]);
            return simpleResultErrorTechnicalError(t);
          } else {
            setErrors([
              t((assignDocksResult?.error as ITranslatableItem).translationKey),
            ]);
            return castErrorResult(assignDocksResult);
          }
        }

        Log.successAndToast(t("ride.docksAssignedSuccessfully"));
        Log.debug(
          "Rides with assigned docks dto received: ",
          assignDocksResult.data,
        );
        synchronizeSelectedRowsOnSearch(
          assignDocksResult.data as IRideTableDto[],
          composedQueryObject,
        );
        replaceRides(assignDocksResult.data);

        return new SimpleResult({ success: true });
      } catch (error) {
        if (axios.isCancel(error)) {
          Log.debug("Axios, request canceled", error);
          return new SimpleResult({
            error: normalizeErrors(error as TError, t),
          });
        }

        Log.errorAndToast(t("errors.failedFetchingRides"), error);

        setErrors(normalizeErrors(error as IApiError, t));

        return new SimpleResult({
          error: normalizeErrors(error as IApiError, t),
        });
      }
    },
  );

  const updateRideDriverOrVehicle = useEvent(
    async ({
      rideId,
      isReturnDtoForGrid,
      patchedFields,
    }: {
      rideId: number;
      isReturnDtoForGrid: boolean;
      patchedFields: RideUpdateDriverOrVehiclePatch;
    }): Promise<SimpleResult<Partial<IRideDto>>> => {
      try {
        if (!hasKeys(patchedFields)) {
          return createErrorResult(errors.common.invalidEntityEmptyObject);
        }

        setRideLoading(true);
        const rideDataForApiSend: TRideUpdateDriverOrVehicleDto = {
          ...patchedFields,
          isReturnDtoForGrid,
        };

        let rideUpdatedResult = await RidesApi.patchRideFields({
          rideId,
          rideDataForApiSend,
        });

        if (isSoftValidationSingleErrorResponse(rideUpdatedResult?.data)) {
          setRideLoading(false);

          const softValidationResult = await confirmSoftValidationAction(
            rideUpdatedResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return new SimpleResult({ success: true });
          }

          setRideLoading(true);
          rideDataForApiSend.token = rideUpdatedResult.data.token;
          rideUpdatedResult = await RidesApi.patchRideFields({
            rideId,
            rideDataForApiSend,
          });
        } else if (
          isSoftHardValidationSingleErrorResponse(rideUpdatedResult?.data)
        ) {
          const hardValidationError =
            rideUpdatedResult.data.validationErrors[0];
          setRideLoading(false);
          return mapHardValidationErrorToResult(hardValidationError);
        }

        if (!rideUpdatedResult) {
          setErrors([t(errors.common.emptyApiResponse)]);
          return simpleResultErrorEmptyResponse(t);
        }

        if (!rideUpdatedResult.success) {
          return castErrorResult(rideUpdatedResult);
        }

        if (!rideUpdatedResult.data) {
          return simpleResultInvalidExecutionResult(t);
        }

        const rideUpdatedDto = rideUpdatedResult.data as IRideDto;
        replaceRide(rideUpdatedDto);
        if (isReturnDtoForGrid) {
          clearStaleRideEditDtoCacheForRide(rideId);
        }
        Log.debug("Ride updated successfully");
        return createSuccessResult(rideUpdatedDto);
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForAction,
          );
          setErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRideLoading(false);
      }
    },
  );

  return {
    renewRide,
    fetchRides,
    refetchRide,
    createRide,
    cancelRide,
    deleteRide,
    updateRide,
    updateRideStartEndDate,
    assignDocks,
    updateRideDriverOrVehicle,
  };
};
